import type { TableMenuItem } from '@/components_deprecated'
import { SortableHeader, Table, TableMenu } from '@/components_deprecated'
import { useInfringementsMutations } from '@/modules/infringements/mutations.hooks'
import { IssueStatusCell } from '@/modules/issues/components/table/components'
import {
  useAdmin,
  useInfiniteScroll,
  useOnErrorAuto,
  usePermissions,
  useUser,
} from '@/utils/hooks'
import { DEFAULT_PAGE_SIZE } from '@/utils/pagination'
import { PERMISSIONS } from '@/utils/permissions'
import { NetworkStatus, useQuery } from '@apollo/client'
import { usePrompt } from '@ur-solutions/react-components'
import { useDebounce, useTranslate } from '@ur-solutions/react-hooks'
import { addDays, formatISO, startOfDay } from 'date-fns'
import React, { useCallback, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import { useInfringementCategoryDescription } from '../../category-descriptions'
import { ALL_INFRINGEMENTS_QUERY } from '../../queries'
import { InfringementStatuses } from '../../consts'
import type {
  ArchivedInfringementForm,
  InfringementsTableFilter,
  InfringementStatus,
} from '../../types'
import type { InfringementNodeForAllInfringements } from '../../types.graphl'
import { ArchiveInfringementPrompt } from './ArchiveInfringementModal'
import { DateCell, EmployeeCell, SeverityCell } from './components'
import { InfringementsCards } from './InfringementsCards'
import { getNodes } from '@/utils/graphql'
import { ActionLogTableRowAction } from '@/modules/issues/views/ActionLogTableRow'

interface DescriptionCellProps {
  hasData: boolean
}

const DescriptionCell = styled.td<DescriptionCellProps>`
  position: relative;
  max-width: 300px;
  ${props => props.hasData && 'cursor: auto;'};

  & > div {
    display: flex;
    flex-direction: column;
    overflow: hidden;

    font-size: 1rem;

    span {
      &.description {
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        font-size: 0.9em;
        font-weight: 400;
        color: ${props => props.theme.colors.gray5};
      }
    }

    &.popup {
      display: none;
      min-width: 100%;
      max-width: 100%;
    }
  }

  &:hover > div.popup {
    position: absolute;
    top: -0.5rem;
    left: -0.5rem;
    z-index: 1000;

    display: flex;
    padding: 1.5rem;

    background: white;
    border-radius: ${props => props.theme.sizes.defaultBorderRadius};
    box-shadow: ${props => props.theme.shadow.default};

    span.description {
      white-space: normal;
      color: ${props => props.theme.colors.text.dark};
    }
  }
`

interface InfringementsTableProps {
  filter: InfringementsTableFilter

  onSort: (orderBy: string) => void
  detailPathBase?: string
}

export const InfringementsTable: React.FC<InfringementsTableProps> = ({
  filter,

  onSort,
  detailPathBase = '/infringements',
}) => {
  const categoryDescription = useInfringementCategoryDescription()
  const translations = useTranslate({
    date: 'common.date',
    employee: 'common.employee',
    archived: 'common.archived',
    open: 'common.open-alt',
    openInfringement: 'common.open',
    close: 'common.close',
    closed: 'common.closed',
    category: 'common.category',
    seeDetails: 'common.see-details',
    status: 'common.status',
    started: 'issues.started',
    action: 'common.action',

    noInfringements: 'infringements.no-infringements',
    noResults: 'common.no-results',

    severity: 'infringements.severity',
    archiveInfringement: 'infringements.archive-infringement',
    restoreInfringement: 'infringements.restore-infringement',
  })

  const navigate = useNavigate()

  const me = useUser()
  const admin = useAdmin()
  const { hasPermissions } = usePermissions()
  const onErrorAuto = useOnErrorAuto()
  const addPrompt = usePrompt()

  const includeStatuses = useMemo<InfringementStatus[]>(
    () => (filter.archived ? [InfringementStatuses.ARCHIVED] : filter.statuses),
    [filter.archived, filter.statuses]
  )
  const debouncedStatuses = useDebounce(includeStatuses)
  const debouncedExcludeSeverities = useDebounce(filter.excludeSeverities, 500)
  const debouncedQuery = useDebounce(filter.query)

  const canViewOthers = hasPermissions(
    PERMISSIONS.infringements.view.othersinfringement,
    true
  )

  const [infringementLoading, setInfringementLoading] = useState<string | null>(
    null
  )

  const canEdit =
    admin || hasPermissions(PERMISSIONS.infringements.change.infringement)

  const { datetimeStartGte, datetimeEndLt } = useMemo(() => {
    const datetimeStartGte = startOfDay(filter.dateRange.from)
    const datetimeEndLt = addDays(startOfDay(filter.dateRange.to), 1)

    return {
      datetimeStartGte,
      datetimeEndLt,
    }
  }, [filter.dateRange.from, filter.dateRange.to])

  const {
    data,
    loading: queryLoading,
    networkStatus,
    fetchMore,
  } = useQuery(ALL_INFRINGEMENTS_QUERY, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      q: debouncedQuery,
      orderBy: filter.orderBy,
      first: DEFAULT_PAGE_SIZE,
      users: canViewOthers ? filter.users : [me.id],
      includeStatuses: debouncedStatuses,
      excludeSeverities: debouncedExcludeSeverities,
      excludeCategories: filter.excludeCategories,
      infringementTimestamp_Lt: formatISO(datetimeEndLt),
      infringementTimestamp_Gte: formatISO(datetimeStartGte),
      ...(canViewOthers &&
        filter.users.length === 0 &&
        filter.leaderGroup !== null && {
          leaderGroupId: filter.leaderGroup,
        }),
    },
    onError: onErrorAuto(),
  })
  const cacheLoading: boolean =
    (queryLoading && !data) || networkStatus === NetworkStatus.refetch

  const infringements = getNodes(data?.allInfringements)
  const pageInfo = data?.allInfringements.pageInfo

  const {
    toggleInfringementArchived,
    loading: archiveInfringementLoading,
    toggleInfringementClosed,
  } = useInfringementsMutations()

  async function handleArchiveInfringement(
    infringement?: InfringementNodeForAllInfringements
  ) {
    if (typeof infringement === 'undefined') return

    if (infringement.archivedAt !== null) {
      void executeArchiveInfringement(infringement)()
      return
    }

    const initialData: ArchivedInfringementForm = {
      archivedBasis: '',
    }

    const { data } = await addPrompt<ArchivedInfringementForm | null>(
      resolve => (
        <ArchiveInfringementPrompt
          initialData={initialData}
          onResolve={resolve}
        />
      )
    )
    if (data === null) return

    void executeArchiveInfringement(infringement)(data)
  }

  function executeArchiveInfringement(
    infringement?: InfringementNodeForAllInfringements | null
  ) {
    if (!infringement) return () => void 0

    return async (data?: ArchivedInfringementForm) => {
      const isArchived = infringement.archivedAt !== null
      setInfringementLoading(infringement.id)

      await toggleInfringementArchived({
        variables: {
          id: infringement.id,
          input: {
            archivedBy: me.id,
            archivedAt: isArchived ? null : formatISO(new Date()),
            archivedBasis: data?.archivedBasis,
          },
        },
      })
      setInfringementLoading(null)
    }
  }

  async function handleCloseInfringement(
    infringement?: InfringementNodeForAllInfringements
  ) {
    if (typeof infringement === 'undefined') return

    void executeCloseInfringement(infringement)()
    return
  }

  function executeCloseInfringement(
    infringement?: InfringementNodeForAllInfringements | null
  ) {
    if (!infringement) return () => void 0

    return async () => {
      const isClosed =
        infringement.closedAt !== null || infringement.hasClosedIssue
      setInfringementLoading(infringement.id)

      await toggleInfringementClosed({
        variables: {
          id: infringement.id,
          input: {
            closedBy: me.id,
            closedAt: isClosed ? null : formatISO(new Date()),
          },
        },
      })
      setInfringementLoading(null)
    }
  }

  const handleScrollBottom = useCallback(async () => {
    if (
      typeof data === 'undefined' ||
      typeof pageInfo === 'undefined' ||
      !pageInfo.hasNextPage
    )
      return
    try {
      await fetchMore({
        variables: {
          after: pageInfo.endCursor,
        },
      })
    } catch (e) {
      if (e instanceof Error && e.name === 'Invariant Violation') return
      throw e
    }
  }, [data, fetchMore, pageInfo])

  useInfiniteScroll(
    handleScrollBottom,
    200,
    !queryLoading && pageInfo?.hasNextPage
  )

  function createMenuItems(infringement: InfringementNodeForAllInfringements) {
    const items: TableMenuItem<InfringementNodeForAllInfringements>[] = [
      {
        label: translations.seeDetails,
        data: infringement,
        role: 'link',
        onClick: () => void navigate(`${detailPathBase}/${infringement.id}`),
      },
    ]

    if (canEdit) {
      const isArchived = infringement.archivedAt !== null
      const isClosed =
        infringement.closedAt !== null || infringement.hasClosedIssue
      items.push({
        label: isArchived
          ? translations.restoreInfringement
          : translations.archiveInfringement,
        data: infringement,
        role: 'link',
        onClick: handleArchiveInfringement,
      })
      items.push({
        label: isClosed ? translations.openInfringement : translations.close,
        data: infringement,
        role: 'link',
        onClick: handleCloseInfringement,
      })
    }
    return items
  }

  const filterActive = debouncedQuery !== ''
  const noDataText = filterActive
    ? translations.noResults
    : translations.noInfringements

  return (
    <>
      {isMobile ? (
        <InfringementsCards
          infringements={infringements}
          queryLoading={queryLoading}
          infringementLoading={infringementLoading}
          createMenuItems={createMenuItems}
          detailPathBase={detailPathBase}
          onLoadMore={handleScrollBottom}
        />
      ) : (
        <Table
          loading={cacheLoading || archiveInfringementLoading}
          loaderProps={{
            columns: 3,
            rows: 10,
            expectedWidths: [92, 82, 162],
          }}
          noData={infringements.length === 0}
          noDataText={noDataText}
        >
          <thead>
            <tr>
              <SortableHeader
                baseValue="infringementTimestamp"
                sortValue={filter.orderBy}
                invertDirection
                onSort={onSort}
              >
                {translations.date}
              </SortableHeader>
              <SortableHeader
                baseValue="user__firstName"
                sortValue={filter.orderBy}
                disabled={!admin}
                onSort={onSort}
              >
                {translations.employee}
              </SortableHeader>

              <th>{translations.category}</th>

              <SortableHeader
                baseValue="infringementCategory__severity"
                sortValue={filter.orderBy}
                onSort={onSort}
              >
                {translations.severity}
              </SortableHeader>
              <th>{translations.status}</th>
              <th>{translations.action}</th>
              <th></th>
            </tr>
          </thead>

          <tbody>
            {infringements.map(infringement => {
              const menuItems = createMenuItems(infringement)

              const description = categoryDescription(
                infringement.infringementCategory.identifier,
                infringement.infringementCategory.description
              )
              const hasData = !!description

              return (
                <tr key={infringement.id}>
                  <DateCell
                    infringementId={infringement.id}
                    createdAt={infringement.infringementTimestamp}
                    detailPathBase={detailPathBase}
                  />

                  <EmployeeCell user={infringement.user} />

                  <DescriptionCell hasData={hasData}>
                    <div className="flat">
                      <span className="description">
                        <b>{infringement.infringementCategory.identifier}.</b>{' '}
                        {description}
                      </span>
                    </div>
                    {hasData && (
                      <div className="popup">
                        <span className="description">
                          <b>{infringement.infringementCategory.identifier}.</b>{' '}
                          {description}
                        </span>
                      </div>
                    )}
                  </DescriptionCell>

                  <SeverityCell
                    severity={infringement.infringementCategory.severity}
                  />
                  <IssueStatusCell
                    closed={infringement.status === InfringementStatuses.CLOSED}
                    archived={
                      infringement.status === InfringementStatuses.ARCHIVED
                    }
                    edited={
                      infringement.status === InfringementStatuses.STARTED
                    }
                    editText={translations.started}
                    openText={translations.open}
                    archivedText={translations.archived}
                    closedText={translations.closed}
                  />

                  {infringement.latestActionLog ? (
                    <ActionLogTableRowAction
                      node={{
                        id: infringement.latestActionLog.id,
                        action: infringement.latestActionLog.action,
                        createdAt: infringement.latestActionLog.createdAt,
                        user: infringement.latestActionLog.user ?? undefined,
                        secondaryUser:
                          infringement.latestActionLog.secondaryUser ??
                          undefined,
                      }}
                    />
                  ) : (
                    <DescriptionCell hasData={false}></DescriptionCell>
                  )}

                  <TableMenu items={menuItems} loading={queryLoading} />
                </tr>
              )
            })}
          </tbody>
        </Table>
      )}
    </>
  )
}
